import React, { useState } from 'react';

const YouTubeVideoPlayer = ({ videoUrl, title }) => {
  const getVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getVideoId(videoUrl);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [hover, setHover] = useState(false);

  if (!videoId) {
    return (
      <div className="bg-red-900/80 border border-red-600 text-red-100 px-6 py-4 rounded-lg backdrop-blur-sm">
        Invalid YouTube URL provided
      </div>
    );
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&color=white`;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Video Title */}
      <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 text-center leading-tight tracking-tight">
        {title}
      </h3>
      
      {/* Video Container */}
      <div 
        className="relative w-full rounded-xl overflow-hidden shadow-2xl transition-all duration-300"
        style={{ paddingBottom: '56.25%' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {/* Thumbnail with cinematic overlay */}
        {!playerLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <img 
              src={thumbnailUrl} 
              alt="Video thumbnail" 
              className={`absolute w-full h-full object-cover transition-opacity duration-500 ${hover ? 'opacity-50' : 'opacity-70'}`}
              onError={(e) => {
                e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            
            {/* Animated play button */}
            <button 
              className={`relative z-10 bg-red-600 hover:bg-red-700 text-white rounded-full p-5 transition-all duration-300 ${hover ? 'scale-110 shadow-lg shadow-red-500/30' : 'scale-100'}`}
              onClick={() => setPlayerLoaded(true)}
              aria-label="Play video"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 sm:h-10 sm:w-10" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Watch Now
              </span>
            </button>
          </div>
        )}
        
        {/* YouTube Iframe with glassmorphism effect */}
        {playerLoaded && (
          <div className="absolute inset-0 rounded-xl overflow-hidden border border-white/10 backdrop-blur-sm">
            <iframe
              className="w-full h-full"
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default YouTubeVideoPlayer;